import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { WalkForwardConfig, WalkForwardResult } from '../types/walkForward';
import { useBacktestConfigurationStore } from '../store/configurationStore';
import { API_CONFIG } from '../services/api/endpoints';
import type { TradingMode } from '../components/backtest/TradingModeSelector';

interface BacktestResult {
    stats: {
        totalReturn: number;
        sharpeRatio: number;
        winRate: number;
        maxDrawdown: number;
        maxDrawdownPercent: number;
        tradeCount: number;
    };
    trades: Array<{
        _id: string;
        symbol: string;
        tradeType: 'long' | 'short';
        avgEntry: number;
        avgExit: number;
        openedAt: string;
        closedAt: string;
        portfolioAfterTrade: number;
        totalProfit: number;
    }>;
    signals: Array<{
        timestamp: string;
        price: number;
        type: 'buy' | 'sell' | 'entry' | 'exit';
        quantity?: number;
        reason?: string;
    }>;
    parameters?: Record<string, number | string>; // Added: Strategy parameters used for this backtest
    dateRange?: {
        startDate: string;
        endDate: string;
    }; // Added: Date range for the backtest
    equity: Array<{
        timestamp: string;
        value: number;
        drawdownPercent: number;
    }>;
    errors?: Array<{
        timestamp: string;
        message: string;
        severity: 'error' | 'warning';
    }>;
}

interface OptimizationResultSummary {
    parameters: Record<string, number>;
    metrics: Record<string, number>;
    score: number;
}

interface OptimizationResults {
    bestResult: BacktestResult;
    allResults: OptimizationResultSummary[]; // Changed from BacktestResult[] to lightweight summaries
    parameters: Record<string, number>;
    optimizationMetrics: {
        totalCombinations: number;
        completedCombinations: number;
        bestPerformance: number;
        averagePerformance: number;
    };
    // Optimization metadata for heatmap and save functionality
    parameterRanges?: Record<string, { min: number; max: number; step: number }>;
    optimizationTarget?: 'totalReturn' | 'sharpeRatio' | 'winRate' | 'profitFactor' | 'maxDrawdown' | 'all';
    candleRangeMetadata?: {
        startCandle: number;
        endCandle: number;
        startDate: string;
        endDate: string;
    };
    // Job ID for linking saved results back to the optimization job
    jobId?: string;
}

interface BacktestContextType {
    currentResult: BacktestResult | null;
    optimizationResults: OptimizationResults | null;
    isRunning: boolean;
    tradingMode: TradingMode;
    setCurrentResult: (result: BacktestResult | null) => void;
    setOptimizationResults: (results: OptimizationResults | null) => void;
    setIsRunning: (isRunning: boolean) => void;
    setTradingMode: (mode: TradingMode) => void;

    // Walk Forward Analysis state
    walkForwardConfig: WalkForwardConfig | null;
    walkForwardResults: WalkForwardResult | null;
    walkForwardRunning: boolean;
    walkForwardProgress: {
        current: number;
        total: number;
        percentage: number;
        phase: string;
    } | null;
    wfaConfigValid: boolean;
    setWalkForwardConfig: (config: WalkForwardConfig | null) => void;
    setWalkForwardResults: (results: WalkForwardResult | null) => void;
    setWalkForwardRunning: (running: boolean) => void;
    setWfaConfigValid: (valid: boolean) => void;
    runWalkForwardAnalysis: () => Promise<void>;
}

const BacktestContext = createContext<BacktestContextType | undefined>(undefined);

export const useBacktestContext = () => {
    const context = useContext(BacktestContext);
    if (context === undefined) {
        throw new Error('useBacktestContext must be used within a BacktestProvider');
    }
    return context;
};

export const BacktestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentResult, setCurrentResult] = useState<BacktestResult | null>(null);
    const [_optimizationResults, _setOptimizationResults] = useState<OptimizationResults | null>(null);

    // Wrapped setter with logging
    const setOptimizationResults = (results: OptimizationResults | null) => {
        _setOptimizationResults(results);
    };

    const optimizationResults = _optimizationResults;

    const [isRunning, setIsRunning] = useState(false);
    const [tradingMode, setTradingMode] = useState<TradingMode>('LONG_ONLY');

    // Walk Forward Analysis state
    const [walkForwardConfig, setWalkForwardConfig] = useState<WalkForwardConfig | null>(null);
    const [walkForwardResults, setWalkForwardResults] = useState<WalkForwardResult | null>(null);
    const [walkForwardRunning, setWalkForwardRunning] = useState(false);
    const [wfaConfigValid, setWfaConfigValid] = useState(true);
    const [walkForwardProgress, setWalkForwardProgress] = useState<{
        current: number;
        total: number;
        percentage: number;
        phase: string;
    } | null>(null);

    // Get real backtest configuration
    const backtestConfig = useBacktestConfigurationStore();

    // Helper function to determine portfolio currency based on trading mode and symbol
    const getPortfolioCurrencyForTradingMode = (mode: TradingMode, symbol: string): string => {
        if (mode === 'SHORT_ONLY') {
            // For short trading, portfolio currency is the base asset (what we're shorting)
            // BTCUSDT -> BTC, ETHUSDT -> ETH, ETHBTC -> ETH
            const baseAsset = symbol.replace(/USDT$|BTC$|ETH$|BNB$/, '');
            return baseAsset;
        } else {
            // For long trading, portfolio currency is typically the quote currency
            // BTCUSDT -> USDT, ETHBTC -> BTC
            const baseAsset = symbol.replace(/USDT$|BTC$|ETH$|BNB$/, '');
            return symbol.slice(baseAsset.length);
        }
    };

    // Automatically update portfolio currency when trading mode or symbol changes
    useEffect(() => {
        const { portfolioCurrency, setPortfolioCurrency } = useBacktestConfigurationStore.getState();
        const newPortfolioCurrency = getPortfolioCurrencyForTradingMode(tradingMode, backtestConfig.symbol);
        if (newPortfolioCurrency !== portfolioCurrency) {
            setPortfolioCurrency(newPortfolioCurrency);
        }
    }, [tradingMode, backtestConfig.symbol]);

    // Function to fetch real candle data from backend
    const fetchCandleData = async (symbol: string, interval: string, startDate: string, endDate: string) => {
        try {
            // Calculate approximate number of candles needed for the date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

            // Calculate candles needed based on interval
            let candlesPerDay = 1; // default for 1d
            switch (interval) {
                case '1m': candlesPerDay = 1440; break;
                case '5m': candlesPerDay = 288; break;
                case '15m': candlesPerDay = 96; break;
                case '30m': candlesPerDay = 48; break;
                case '1h': candlesPerDay = 24; break;
                case '4h': candlesPerDay = 6; break;
                case '1d': candlesPerDay = 1; break;
                case '1w': candlesPerDay = 1 / 7; break;
            }

            const estimatedCandles = Math.ceil(daysDiff * candlesPerDay);
            // Add buffer and cap at reasonable maximum
            const limit = Math.min(Math.max(estimatedCandles * 1.2, 500), 5000);


            const response = await fetch(`${API_CONFIG.BASE_URL}/candles/${symbol}/${interval}?limit=${limit}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer demo-token`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch candle data: ${response.status} ${response.statusText}`);
            }

            const candleResponse = await response.json();

            if (!candleResponse.success || !candleResponse.data) {
                throw new Error('Invalid candle data response');
            }

            // Filter candles to the actual date range if possible
            let filteredCandles = candleResponse.data;
            if (candleResponse.data.length > 0) {
                filteredCandles = candleResponse.data.filter((candle: any) => {
                    const candleDate = new Date(candle.timestamp);
                    return candleDate >= start && candleDate <= end;
                });

                // If filtering removed too many candles, use all data
                if (filteredCandles.length < 50) {
                    filteredCandles = candleResponse.data;
                }
            }

            return filteredCandles;

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            throw new Error(`Failed to fetch candle data: ${errorMessage}`);
        }
    };

    // WFA API call function with async job handling
    const callWalkForwardAPI = async (config: WalkForwardConfig, strategy: any): Promise<WalkForwardResult> => {
        try {

            // Start the enhanced optimization analysis with WFA configuration
            const response = await fetch(`${API_CONFIG.BASE_URL}/optimize/strategy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer demo-token`
                },
                body: JSON.stringify({
                    strategy: strategy.strategy,
                    symbol: strategy.symbol,
                    interval: strategy.interval,
                    portfolioAmount: strategy.portfolioAmount,
                    portfolioCurrency: strategy.portfolioCurrency,
                    parameters: strategy.params,
                    startDate: strategy.dateRange.start,
                    endDate: strategy.dateRange.end,
                    walkForwardConfig: config,
                    enableProgressUpdates: true
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`WFA API error: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const jobResponse = await response.json();

            // Trigger optimization queue refresh to pick up the new WFA job
            // We'll dispatch a custom event that the OptimizationQueueContext can listen for
            window.dispatchEvent(new CustomEvent('wfa-job-started', {
                detail: { jobId: jobResponse.jobId }
            }));

            // Check if this is a job response (async pattern) or direct results (sync pattern)
            if (jobResponse.jobId && jobResponse.progressEndpoint) {

                // Log the date range being processed

                // Poll progress endpoint until job completes
                const results = await pollWFAJobProgress(jobResponse.jobId, jobResponse.progressEndpoint);
                return results;
            }

            // Handle direct results (legacy/sync pattern)
            const wfaResults = jobResponse.data?.results || jobResponse.results;
            if (wfaResults && wfaResults.trainingMetrics) {

                // Transform backend results to match frontend format
                // Backend returns decimals (0.15 = 15%), frontend expects percentages
                const trainingReturn = (wfaResults.trainingMetrics?.totalReturn || 0) * 100;
                const walkForwardReturn = (wfaResults.walkForwardMetrics?.totalReturn || 0) * 100;

                const realResult: WalkForwardResult = {
                    id: jobResponse.data?.analysisId || jobResponse.id || 'wfa-' + Date.now(),
                    analysisId: jobResponse.data?.analysisId || jobResponse.id || 'analysis-' + Date.now(),
                    trainingMetrics: {
                        totalReturn: trainingReturn,
                        sharpeRatio: wfaResults.trainingMetrics?.sharpeRatio || 0,
                        winRate: (wfaResults.trainingMetrics?.winRate || 0) * 100, // Convert to percentage
                        tradeCount: wfaResults.trainingMetrics?.tradeCount || 0,
                        maxDrawdownPercent: (wfaResults.trainingMetrics?.maxDrawdown || 0) * 100
                    },
                    walkForwardMetrics: {
                        totalReturn: walkForwardReturn,
                        sharpeRatio: wfaResults.walkForwardMetrics?.sharpeRatio || 0,
                        winRate: (wfaResults.walkForwardMetrics?.winRate || 0) * 100,
                        tradeCount: wfaResults.walkForwardMetrics?.tradeCount || 0,
                        maxDrawdownPercent: (wfaResults.walkForwardMetrics?.maxDrawdown || 0) * 100
                    },
                    combinedMetrics: {
                        totalReturn: (wfaResults.combinedMetrics?.totalReturn || 0) * 100,
                        sharpeRatio: wfaResults.combinedMetrics?.sharpeRatio || 0,
                        winRate: (wfaResults.combinedMetrics?.winRate || 0) * 100,
                        tradeCount: wfaResults.combinedMetrics?.tradeCount || 0,
                        maxDrawdownPercent: (wfaResults.combinedMetrics?.maxDrawdown || 0) * 100
                    },
                    overfittingMetrics: {
                        wfe: wfaResults.overfittingMetrics?.wfe || 0,
                        performanceDrop: wfaResults.overfittingMetrics?.performanceDrop || 0,
                        sharpeRatioDrop: wfaResults.overfittingMetrics?.sharpeRatioDrop || 0,
                        winRateDrop: wfaResults.overfittingMetrics?.winRateDrop || 0
                    },
                    splitInfo: {
                        trainingPeriod: { start: '2023-01-01', end: '2023-06-30' },
                        walkForwardPeriod: { start: '2023-07-01', end: '2023-12-31' },
                        splitRatio: config.ratio || '3:1'
                    },
                    createdAt: new Date()
                };

                return realResult;
            }

            // If backend doesn't return expected format, throw error
            throw new Error('Backend returned invalid Walk Forward Analysis response. Expected either jobId+progressEndpoint for async jobs or data.results for sync results.');

        } catch (error) {
            throw error;
        }
    };

    // Poll WFA job progress until completion
    const pollWFAJobProgress = async (jobId: string, progressEndpoint: string): Promise<WalkForwardResult> => {
        const maxAttempts = 240; // 20 minutes max (5s intervals) - increased for long-running WFA
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {

                const token = localStorage.getItem('authToken');
                const progressResponse = await fetch(`${API_CONFIG.BASE_URL}${progressEndpoint.replace(/^\/api/, '')}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!progressResponse.ok) {
                    throw new Error(`Progress poll failed: ${progressResponse.status}`);
                }

                const progressData = await progressResponse.json();

                // Handle different response formats from backend
                let jobStatus = null;
                let results = null;

                if (progressData.data) {
                    // Format: { success: true, data: { status, results?, progress?, ... } }
                    jobStatus = progressData.data.status;
                    results = progressData.data.results;

                    // Enhanced debugging for immediate completion

                    // Log progress details if available
                    if (progressData.data.progress !== undefined) {
                        const current = progressData.data.current || progressData.data.currentPeriod || 0;
                        const total = progressData.data.total || progressData.data.totalPeriods || 0;
                        const percentage = progressData.data.progress || 0;
                        const phase = progressData.data.currentPhase || 'Processing...';

                        // Update UI progress state
                        setWalkForwardProgress({
                            current,
                            total,
                            percentage,
                            phase
                        });

                    }
                } else {
                    // Direct format: { status, results?, ... }
                    jobStatus = progressData.status;
                    results = progressData.results;
                }

                // Check for immediate completion (potential issue)
                if (jobStatus === 'completed' && results) {

                    // Check if this looks like mock data
                    const rawTrainingReturn = results.trainingMetrics?.totalReturn;
                    const rawWalkForwardReturn = results.walkForwardMetrics?.totalReturn;

                    if (rawTrainingReturn === 0.15 && rawWalkForwardReturn === 0.12) {
                    } else if (attempts === 0) {

                        // Additional diagnostics for immediate completion

                        // Check if this is actually a cached result
                        const analysisStart = progressData.data?.startTime;
                        const analysisEnd = progressData.data?.completedAt;
                        if (analysisStart && analysisEnd) {
                            const analysisTimeMs = new Date(analysisEnd).getTime() - new Date(analysisStart).getTime();
                            if (analysisTimeMs < 1000) {
                            }
                        }
                    } else {
                    }

                    // Transform WFA results to expected format
                    // Backend returns: { periods, summary, periodResults, aggregatedMetrics, ... }

                    // Log configuration and date range info

                    // Log period details
                    if (results.periods && results.periods.length > 0) {
                        const firstPeriod = results.periods[0];
                        const lastPeriod = results.periods[results.periods.length - 1];
                    }

                    // Extract metrics from aggregatedMetrics or summary
                    const aggregated = results.aggregatedMetrics || {};
                    const summary = results.summary || {};


                    // Helper function to extract value from aggregated structure
                    const extractValue = (metricObj: any, fallback = 0): number => {
                        if (typeof metricObj === 'number') return metricObj;
                        if (metricObj && typeof metricObj === 'object') {
                            return metricObj.average || metricObj.value || metricObj.mean || fallback;
                        }
                        return fallback;
                    };

                    // Get training period metrics (inSample in backend)
                    const rawTraining = aggregated.inSample || aggregated.training || {};
                    const trainingMetrics = {
                        totalReturn: extractValue(rawTraining.totalReturn, summary.trainingReturn || 0),
                        sharpeRatio: extractValue(rawTraining.sharpeRatio, summary.trainingSharpe || 0),
                        winRate: extractValue(rawTraining.winRate, summary.trainingWinRate || 0),
                        tradeCount: extractValue(rawTraining.totalTrades || rawTraining.tradeCount, summary.trainingTrades || 0),
                        maxDrawdown: extractValue(rawTraining.maxDrawdown, summary.trainingMaxDD || 0)
                    };

                    // Get walk-forward period metrics (outSample in backend)
                    const rawWalkForward = aggregated.outSample || aggregated.walkForward || {};
                    const walkForwardMetrics = {
                        totalReturn: extractValue(rawWalkForward.totalReturn, summary.walkForwardReturn || summary.outOfSampleReturn || 0),
                        sharpeRatio: extractValue(rawWalkForward.sharpeRatio, summary.walkForwardSharpe || summary.outOfSampleSharpe || 0),
                        winRate: extractValue(rawWalkForward.winRate, summary.walkForwardWinRate || summary.outOfSampleWinRate || 0),
                        tradeCount: extractValue(rawWalkForward.totalTrades || rawWalkForward.tradeCount, summary.walkForwardTrades || summary.outOfSampleTrades || 0),
                        maxDrawdown: extractValue(rawWalkForward.maxDrawdown, summary.walkForwardMaxDD || summary.outOfSampleMaxDD || 0)
                    };

                    // Backend returns decimals (0.15 = 15%) for percentages, but large numbers for returns
                    // Check if returns are already percentages (>1) or decimals (<1)
                    const isTrainingReturnPercent = trainingMetrics.totalReturn > 1;
                    const isWalkForwardReturnPercent = walkForwardMetrics.totalReturn > 1;

                    const trainingReturn = isTrainingReturnPercent ? trainingMetrics.totalReturn : (trainingMetrics.totalReturn * 100);
                    const walkForwardReturn = isWalkForwardReturnPercent ? walkForwardMetrics.totalReturn : (walkForwardMetrics.totalReturn * 100);

                    const wfaResult: WalkForwardResult = {
                        id: jobId,
                        analysisId: jobId,
                        trainingMetrics: {
                            totalReturn: trainingReturn,
                            sharpeRatio: trainingMetrics.sharpeRatio || 0,
                            winRate: (trainingMetrics.winRate || 0) * 100,
                            tradeCount: trainingMetrics.tradeCount || 0,
                            maxDrawdownPercent: (trainingMetrics.maxDrawdown || 0) * 100
                        },
                        walkForwardMetrics: {
                            totalReturn: walkForwardReturn,
                            sharpeRatio: walkForwardMetrics.sharpeRatio || 0,
                            winRate: (walkForwardMetrics.winRate || 0) * 100,
                            tradeCount: walkForwardMetrics.tradeCount || 0,
                            maxDrawdownPercent: (walkForwardMetrics.maxDrawdown || 0) * 100
                        },
                        combinedMetrics: {
                            totalReturn: ((trainingReturn + walkForwardReturn) / 2),
                            sharpeRatio: ((trainingMetrics.sharpeRatio || 0) + (walkForwardMetrics.sharpeRatio || 0)) / 2,
                            winRate: ((trainingMetrics.winRate || 0) + (walkForwardMetrics.winRate || 0)) / 2 * 100,
                            tradeCount: (trainingMetrics.tradeCount || 0) + (walkForwardMetrics.tradeCount || 0),
                            maxDrawdownPercent: Math.max((trainingMetrics.maxDrawdown || 0), (walkForwardMetrics.maxDrawdown || 0)) * 100
                        },
                        overfittingMetrics: {
                            wfe: results.overfittingMetrics?.wfe || 0,
                            performanceDrop: results.overfittingMetrics?.performanceDrop || 0,
                            sharpeRatioDrop: results.overfittingMetrics?.sharpeRatioDrop || 0,
                            winRateDrop: results.overfittingMetrics?.winRateDrop || 0
                        },
                        splitInfo: {
                            trainingPeriod: { start: '2023-01-01', end: '2023-06-30' },
                            walkForwardPeriod: { start: '2023-07-01', end: '2023-12-31' },
                            splitRatio: '3:1'
                        },
                        createdAt: new Date(),

                        // Include raw backend data for debugging and extended analysis
                        periods: results.periods || [],
                        summary: results.summary || {},
                        aggregatedMetrics: results.aggregatedMetrics || {},
                        periodResults: results.periodResults || [],
                        configuration: results.configuration || {},
                        totalPeriods: results.totalPeriods || 0,
                        successfulPeriods: results.successfulPeriods || 0,
                        rawResults: results // Keep original for debugging
                    };

                    return wfaResult;
                }

                if (jobStatus === 'failed') {
                    throw new Error(`WFA job failed: ${results?.error || progressData.data?.error || 'Unknown error'}`);
                }

                // Job still running, check if we have progress info
                if (jobStatus === 'running' || jobStatus === 'started' || jobStatus === 'in_progress') {
                } else if (!jobStatus) {
                } else {
                }

                // Wait and retry
                await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
                attempts++;

            } catch (error) {
                attempts++;

                if (attempts >= maxAttempts) {
                    throw new Error(`WFA job polling timeout after ${maxAttempts} attempts (${maxAttempts * 5 / 60} minutes)`);
                }

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        }

        throw new Error(`WFA job polling timeout - job did not complete within expected time (${maxAttempts * 5 / 60} minutes)`);
    }; const runWalkForwardAnalysis = async () => {
        if (!walkForwardConfig) {
            return;
        }

        setWalkForwardRunning(true);
        setWalkForwardResults(null);
        setWalkForwardProgress(null); // Clear previous progress

        try {
            // Get current strategy configuration
            const {
                strategy,
                symbol,
                interval,
                portfolioAmount,
                portfolioCurrency,
                parameterRanges,
                startDate,
                endDate
            } = backtestConfig;

            if (!strategy || !symbol) {
                throw new Error('Please configure strategy and trading pair first');
            }

            // If dates are empty, fetch the full available date range from database
            let effectiveStartDate = startDate;
            let effectiveEndDate = endDate;

            if (!effectiveStartDate || !effectiveEndDate) {

                try {
                    // Fetch available date range for this symbol/interval
                    const statsResponse = await fetch(`${API_CONFIG.BASE_URL}/candles-stats`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer demo-token`
                        }
                    });

                    if (statsResponse.ok) {
                        const stats = await statsResponse.json();
                        if (stats.success && stats.data) {
                            // Find stats for our symbol/interval
                            const symbolStats = stats.data.find((stat: any) =>
                                stat._id.symbol === symbol && stat._id.interval === interval
                            );

                            if (symbolStats && symbolStats.minDate && symbolStats.maxDate) {
                                effectiveStartDate = new Date(symbolStats.minDate).toISOString().split('T')[0];
                                effectiveEndDate = new Date(symbolStats.maxDate).toISOString().split('T')[0];
                            }
                        }
                    }
                } catch (error) {
                }

                // Fallback to default dates if database query failed
                if (!effectiveStartDate || !effectiveEndDate) {
                    effectiveStartDate = '2024-01-01';
                    effectiveEndDate = '2024-12-31';
                }
            }

            // Fetch real candle data for the strategy
            const candleData = await fetchCandleData(symbol, interval, effectiveStartDate, effectiveEndDate);



            // For WFA, send parameter ranges instead of individual parameter values
            // The WFA service will run optimization to find the best parameters for each period


            // Prepare strategy payload for WFA API
            const strategyPayload = {
                strategy: strategy,
                symbol,
                interval,
                portfolioAmount,
                portfolioCurrency,
                parameterRanges: parameterRanges, // Send ranges for optimization, not individual values
                dateRange: {
                    start: effectiveStartDate,
                    end: effectiveEndDate
                },
                // Use real candle data instead of mock data
                data: candleData
            };



            // Create enhanced config with required date fields for API
            const enhancedWalkForwardConfig = {
                ...walkForwardConfig,
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
                trainingStart: effectiveStartDate,
                walkForwardEnd: effectiveEndDate
            };


            // Use direct API call instead of WebSocket integration
            const result = await callWalkForwardAPI(enhancedWalkForwardConfig, strategyPayload);

            setWalkForwardResults(result);

        } catch (error) {
        } finally {
            setWalkForwardRunning(false);
            setWalkForwardProgress(null); // Clear progress when done
        }
    };

    return (
        <BacktestContext.Provider value={{
            currentResult,
            optimizationResults,
            isRunning,
            tradingMode,
            setCurrentResult,
            setOptimizationResults,
            setIsRunning,
            setTradingMode,
            walkForwardConfig,
            walkForwardResults,
            walkForwardRunning,
            walkForwardProgress,
            wfaConfigValid,
            setWalkForwardConfig,
            setWalkForwardResults,
            setWalkForwardRunning,
            setWfaConfigValid,
            runWalkForwardAnalysis
        }}>
            {children}
        </BacktestContext.Provider>
    );
};

// Export types for use in other components
export type { BacktestResult, OptimizationResults, OptimizationResultSummary };
